import React, { useEffect, useState } from "react";
import { adminApi } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Mail, Trash2, Check, Circle, Clock, Building2, Globe, Package } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function AdminInbox({ kind = "rfq" }) {
  const { toast } = useToast();
  const tab = kind; // "rfq" | "messages"
  const [rfqs, setRfqs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        adminApi.get("/admin/rfq"),
        adminApi.get("/admin/messages"),
      ]);
      setRfqs(r.data);
      setMessages(m.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const items = tab === "rfq" ? rfqs : messages;
  const endpoint = tab === "rfq" ? "rfq" : "messages";

  const markRead = async (item) => {
    if (item.read) return;
    await adminApi.patch(`/admin/${endpoint}/${item.id}`, { read: true });
    if (tab === "rfq") setRfqs((p) => p.map((x) => x.id === item.id ? { ...x, read: true } : x));
    else setMessages((p) => p.map((x) => x.id === item.id ? { ...x, read: true } : x));
    window.dispatchEvent(new Event("admin-counts-changed"));
  };

  const remove = async (id) => {
    await adminApi.delete(`/admin/${endpoint}/${id}`);
    toast({ title: "Deleted" });
    setSelected(null);
    if (tab === "rfq") setRfqs((p) => p.filter((x) => x.id !== id));
    else setMessages((p) => p.filter((x) => x.id !== id));
    window.dispatchEvent(new Event("admin-counts-changed"));
  };

  const openItem = (item) => { setSelected(item); markRead(item); };

  return (
    <div>
      <h1 className="font-display text-3xl text-[#0f1e33]">{tab === "rfq" ? "RFQ Enquiries" : "Messages"}</h1>
      <p className="text-[#5a6b7e] mt-1">
        {tab === "rfq" ? "Quotation requests submitted through the website." : "Messages sent from the Contact page."}
      </p>

      <div className="mt-6 grid lg:grid-cols-5 gap-5">
        {/* List */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-border overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#5a6b7e]">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-[#9aa8b5]">No {tab === "rfq" ? "enquiries" : "messages"} yet.</div>
          ) : (
            <ul className="divide-y divide-border max-h-[70vh] overflow-auto">
              {items.map((it) => (
                <li key={it.id}>
                  <button onClick={() => openItem(it)} className={`w-full text-left px-5 py-4 hover:bg-[#f7fafc] transition-colors ${selected?.id === it.id ? "bg-[#eaf4f7]" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-[#0f1e33] truncate flex items-center gap-2">
                        {!it.read && <Circle className="h-2 w-2 fill-[#d9a441] text-[#d9a441]" />}
                        {it.name}
                      </span>
                      <span className="text-[11px] text-[#9aa8b5] shrink-0 flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(it.created_at).split(",")[0]}</span>
                    </div>
                    <p className="text-xs text-[#5a6b7e] mt-1 truncate">{it.email}{it.product ? ` · ${it.product}` : ""}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-border p-6 lg:p-8">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-[#9aa8b5] py-20">Select an item to view details.</div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl text-[#0f1e33]">{selected.name}</h2>
                  <p className="text-sm text-[#5a6b7e] mt-1">{formatDate(selected.created_at)}</p>
                </div>
                <button onClick={() => remove(selected.id)} className="inline-flex items-center gap-1.5 text-sm text-red-500 border border-red-100 rounded-lg px-3 h-9 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Detail icon={Mail} label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#0e7490]">{selected.email}</a>} />
                {selected.company && <Detail icon={Building2} label="Company" value={selected.company} />}
                {selected.country && <Detail icon={Globe} label="Country" value={selected.country} />}
                {selected.product && <Detail icon={Package} label="Product" value={selected.product} />}
              </div>

              {selected.items && selected.items.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#9aa8b5] mb-2">Enquiry list</p>
                  <div className="flex flex-wrap gap-2">
                    {selected.items.map((it, i) => (
                      <span key={i} className="text-xs bg-[#eaf4f7] text-[#0e7490] px-2.5 py-1 rounded-full">{it.name}</span>
                    ))}
                  </div>
                </div>
              )}

              {selected.message && (
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#9aa8b5] mb-2">Message</p>
                  <p className="text-[15px] text-[#33445a] leading-relaxed whitespace-pre-wrap bg-[#f7fafc] rounded-xl p-4 border border-border">{selected.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="h-9 w-9 rounded-lg bg-[#eaf4f7] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#0e7490]" /></span>
      <div>
        <p className="text-xs text-[#9aa8b5]">{label}</p>
        <p className="text-sm font-medium text-[#0f1e33] mt-0.5">{value}</p>
      </div>
    </div>
  );
}
